import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { 
  Table, 
  Input, 
  InputNumber,
  Select, 
  Slider, 
  Tag, 
  Space, 
  Button, 
  Card,
  Row,
  Col,
  Tooltip,
  ConfigProvider,
  message,
  Modal,
  Statistic,
  Badge,
  Typography,
  Tabs,
  Progress,
  Timeline,
  Divider,
  Avatar,
  List,
  Alert,
  Switch
} from 'antd';
import { 
  SearchOutlined, 
  FilterOutlined,
  DollarOutlined,
  StarOutlined,
  PercentageOutlined,
  TrophyOutlined,
  RiseOutlined,
  BarChartOutlined,
  HomeOutlined,
  SafetyCertificateOutlined,
  CloseOutlined,
  FileTextOutlined,
  TeamOutlined,
  FundOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PieChartOutlined,
  LineChartOutlined,
  ShoppingCartOutlined,
  EyeOutlined,
  TableOutlined,
  AppstoreOutlined,
  MenuOutlined,
  SortAscendingOutlined
} from '@ant-design/icons';
import { Search, Star, DollarSign, Percent, TrendingUp, TrendingDown, ArrowRight, XCircle, CheckCircle, BarChart2, Building2, Shield, Filter, X, Grid3X3, List as ListIcon, LayoutGrid, ArrowUpDown } from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

// Styled Components
const PageContainer = styled(motion.div)`
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
`;

const GlassCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
  border-radius: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 48px 0 rgba(31, 38, 135, 0.2);
  }
`;

const SearchSection = styled(GlassCard)`
  margin-bottom: 16px;
  padding: 0px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 252, 0.95) 100%);
`;

const FiltersSection = styled(motion.div)`
  margin-bottom: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const StatsCard = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.gradient || '#667eea 0%, #764ba2 100%'});
  border-radius: 12px;
  padding: 20px;
  color: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const FilterTag = styled(Tag)`
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 13px;
  border: none;
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
  
  .ant-tag-close-icon {
    color: #6366f1;
    margin-left: 4px;
  }
`;

const FundCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
  border-radius: 16px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 400px;
  box-sizing: border-box;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 48px 0 rgba(31, 38, 135, 0.25);
  }
`;

const ViewModeButton = styled(Button)`
  border-radius: 8px;
  border: none;
  background: ${props => props.active ? '#6366f1' : 'rgba(255, 255, 255, 0.8)'};
  color: ${props => props.active ? 'white' : '#6366f1'};
  backdrop-filter: blur(8px);
  
  &:hover {
    background: ${props => props.active ? '#5855eb' : 'rgba(99, 102, 241, 0.1)'};
    color: ${props => props.active ? 'white' : '#5855eb'};
  }
`;

// --- MOCK COMPANY IPS CRITERIA ---
const mockCompanies = [
  {
    id: "tech_corp",
    name: "TechCorp Industries",
    ips_criteria: {
      max_expense_ratio: 0.010, // 1.0%
      min_assets_millions: 500,
      min_manager_tenure_years: 3,
      allow_sales_loads: false,
      allow_12b1_fees: false,
      min_sharpe_ratio_5yr: 0.6,
      max_tracking_error: 0.3,
      min_morningstar_rating: 4
    }
  },
  {
    id: "global_mfg",
    name: "Global Manufacturing Inc",
    ips_criteria: {
      max_expense_ratio: 0.015, // 1.5%
      min_assets_millions: 1000,
      min_manager_tenure_years: 5,
      allow_sales_loads: false,
      allow_12b1_fees: true,
      min_sharpe_ratio_5yr: 0.5,
      max_tracking_error: 0.4,
      min_morningstar_rating: 3
    }
  },
  {
    id: "healthcare_sys",
    name: "Healthcare Systems LLC",
    ips_criteria: {
      max_expense_ratio: 0.008, // 0.8%
      min_assets_millions: 750,
      min_manager_tenure_years: 4,
      allow_sales_loads: false,
      allow_12b1_fees: false,
      min_sharpe_ratio_5yr: 0.7,
      max_tracking_error: 0.25,
      min_morningstar_rating: 4
    }
  },
  {
    id: "financial_group",
    name: "Financial Services Group",
    ips_criteria: {
      max_expense_ratio: 0.012, // 1.2%
      min_assets_millions: 2000,
      min_manager_tenure_years: 6,
      allow_sales_loads: true,
      allow_12b1_fees: true,
      min_sharpe_ratio_5yr: 0.55,
      max_tracking_error: 0.35,
      min_morningstar_rating: 3
    }
  }
];

// --- MOCK DATA ---
const mockFunds = [
  {
    fund_name: "PlanSync Drifting Growth Fund - Instl",
    ticker: "PSDGFX",
    active_or_passive: "Active",
    fund_type: "MF",
    peer_group_details: { name: "Morningstar Large Growth", fund_count: 1250 },
    peer_percentile: { expense_ratio: 48, sharpe_ratio_5yr: 78, alpha_5yr: 72 },
    metrics: {
      expense_ratio: 0.0072, turnover_ratio: 0.65, sharpe_ratio_5yr: 0.92,
      alpha_5yr: 0.0185, r_squared_5yr: 0.91, downside_capture_5yr: 0.98,
      manager_tenure_avg_yrs: 8.5, fund_aum_millions: 2100,
      has_12b1_fee: false, has_sales_load: false, tracking_error_5yr: 0.25,
      share_price: 127.45
    },
    qualitative_flags: { morningstar_parent_pillar: 4 }
  },
  {
    fund_name: "Strategic Bond CIT - Class A",
    ticker: "SBCAX",
    active_or_passive: "Active",
    fund_type: "CIT",
    peer_group_details: { name: "Morningstar Intermediate Bond", fund_count: 892 },
    peer_percentile: { expense_ratio: 25, sharpe_ratio_5yr: 65, alpha_5yr: 55 },
    metrics: {
      expense_ratio: 0.0045, turnover_ratio: 0.32, sharpe_ratio_5yr: 0.68,
      alpha_5yr: 0.0095, r_squared_5yr: 0.85, downside_capture_5yr: 0.78,
      manager_tenure_avg_yrs: 12.2, fund_aum_millions: 850,
      has_12b1_fee: false, has_sales_load: true, tracking_error_5yr: 0.18,
      share_price: 10.87
    },
    qualitative_flags: { morningstar_parent_pillar: 5 }
  },
    {
    fund_name: "Global Value Equity Fund",
    ticker: "GVEFX",
    active_or_passive: "Active",
    fund_type: "MF",
    peer_group_details: { name: "Morningstar Global Large-Cap Value", fund_count: 630 },
    peer_percentile: { expense_ratio: 65, sharpe_ratio_5yr: 45, alpha_5yr: 40 },
    metrics: {
      expense_ratio: 0.0095, turnover_ratio: 0.45, sharpe_ratio_5yr: 0.55,
      alpha_5yr: -0.0010, r_squared_5yr: 0.88, downside_capture_5yr: 1.05,
      manager_tenure_avg_yrs: 5.1, fund_aum_millions: 1200,
      has_12b1_fee: true, has_sales_load: false, tracking_error_5yr: 0.35,
      share_price: 43.92
    },
    qualitative_flags: { morningstar_parent_pillar: 3 }
  },
  {
    fund_name: "Index Universe Tracker Passive",
    ticker: "IUTPX",
    active_or_passive: "Passive",
    fund_type: "MF",
    peer_group_details: { name: "Morningstar US Equity Market", fund_count: 2500 },
    peer_percentile: { expense_ratio: 5, sharpe_ratio_5yr: 88, alpha_5yr: 95 },
    metrics: {
      expense_ratio: 0.0005, turnover_ratio: 0.08, sharpe_ratio_5yr: 1.15,
      alpha_5yr: 0.0005, r_squared_5yr: 0.99, downside_capture_5yr: 1.00,
      manager_tenure_avg_yrs: 10.0, fund_aum_millions: 15000,
      has_12b1_fee: false, has_sales_load: false, tracking_error_5yr: 0.02,
      share_price: 89.33
    },
    qualitative_flags: { morningstar_parent_pillar: 4 }
  }
];

// --- HELPER & FORMATTING FUNCTIONS ---
const formatCurrency = (value) => {
  // Convert millions to appropriate format
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}BB`;
  } else if (value >= 1) {
    return `$${value.toFixed(0)}MM`;
  } else {
    return `$${(value * 1000).toFixed(0)}K`;
  }
};

const formatPercentage = (value, decimals = 2) => {
    if (value === null || isNaN(value)) return 'N/A';
    return `${(value * 100).toFixed(decimals)}%`;
}

const formatYears = (value) => `${value.toFixed(1)} years`;

const formatSharePrice = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatStars = (rating) => {
  const filled = '★'.repeat(rating);
  const empty = '☆'.repeat(5 - rating);
  return `${filled}${empty}`;
};

// --- CORE LOGIC FUNCTIONS ---

// Function to evaluate fund compliance against a company's IPS criteria
const evaluateIPSCompliance = (fund, company) => {
    const criteria = company.ips_criteria;
    const issues = [];
    
    // Check expense ratio
    if (fund.metrics.expense_ratio > criteria.max_expense_ratio) {
        issues.push(`Expense ratio ${formatPercentage(fund.metrics.expense_ratio)} exceeds maximum ${formatPercentage(criteria.max_expense_ratio)}`);
    }
    
    // Check fund assets
    if (fund.metrics.fund_aum_millions < criteria.min_assets_millions) {
        issues.push(`Fund AUM $${fund.metrics.fund_aum_millions}M below minimum $${criteria.min_assets_millions}M`);
    }
    
    // Check manager tenure
    if (fund.metrics.manager_tenure_avg_yrs < criteria.min_manager_tenure_years) {
        issues.push(`Manager tenure ${fund.metrics.manager_tenure_avg_yrs} years below minimum ${criteria.min_manager_tenure_years} years`);
    }
    
    // Check sales loads
    if (fund.metrics.has_sales_load && !criteria.allow_sales_loads) {
        issues.push('Sales loads not permitted by IPS');
    }
    
    // Check 12b-1 fees
    if (fund.metrics.has_12b1_fee && !criteria.allow_12b1_fees) {
        issues.push('12b-1 fees not permitted by IPS');
    }
    
    // Check Sharpe ratio
    if (fund.metrics.sharpe_ratio_5yr < criteria.min_sharpe_ratio_5yr) {
        issues.push(`5-year Sharpe ratio ${fund.metrics.sharpe_ratio_5yr.toFixed(2)} below minimum ${criteria.min_sharpe_ratio_5yr}`);
    }
    
    // Check tracking error
    if (fund.metrics.tracking_error_5yr > criteria.max_tracking_error) {
        issues.push(`Tracking error ${formatPercentage(fund.metrics.tracking_error_5yr)} exceeds maximum ${formatPercentage(criteria.max_tracking_error)}`);
    }
    
    // Check Morningstar rating
    if (fund.qualitative_flags.morningstar_parent_pillar < criteria.min_morningstar_rating) {
        issues.push(`Morningstar rating ${fund.qualitative_flags.morningstar_parent_pillar} below minimum ${criteria.min_morningstar_rating}`);
    }
    
    return {
        compliant: issues.length === 0,
        issues: issues
    };
};

// Function to get IPS compliance summary for a fund across all companies
const getIPSComplianceSummary = (fund) => {
    const complianceResults = mockCompanies.map(company => ({
        company: company,
        compliance: evaluateIPSCompliance(fund, company)
    }));
    
    const compliantCompanies = complianceResults.filter(result => result.compliance.compliant);
    const nonCompliantCompanies = complianceResults.filter(result => !result.compliance.compliant);
    
    return {
        total_companies: mockCompanies.length,
        compliant_count: compliantCompanies.length,
        non_compliant_count: nonCompliantCompanies.length,
        compliant_companies: compliantCompanies,
        non_compliant_companies: nonCompliantCompanies,
        compliance_rate: (compliantCompanies.length / mockCompanies.length) * 100
    };
};

const calculateFundScore = (fund) => {
    // Morningstar Parent Pillar (20% weight) - score is rating / 5 * 20
    const pillarScore = (fund.qualitative_flags.morningstar_parent_pillar / 5) * 20;

    // Expense Ratio Percentile (25% weight) - lower is better
    const expenseScore = (1 - (fund.peer_percentile.expense_ratio / 100)) * 25;

    // Sharpe Ratio (5yr) Percentile (25% weight) - higher is better
    const sharpeScore = (fund.peer_percentile.sharpe_ratio_5yr / 100) * 25;
    
    // Alpha (5yr) Percentile (20% weight) - higher is better
    const alphaScore = (fund.peer_percentile.alpha_5yr / 100) * 20;

    // AUM Bonus (10% weight) - Capped at $5B for max bonus
    const aumBonus = Math.min(fund.metrics.fund_aum_millions / 5000, 1) * 10;
    
    const totalScore = pillarScore + expenseScore + sharpeScore + alphaScore + aumBonus;
    
    let label = "Remove";
    if (totalScore >= 70) label = "Not on Watchlist";
    else if (totalScore >= 50) label = "Watch";

    return {
        score: parseFloat(totalScore.toFixed(1)),
        label: label,
    };
};

const getTopFunds = (funds) => {
    return funds
        .map(fund => ({ 
            ...fund, 
            planSyncScore: calculateFundScore(fund),
            ipsComplianceSummary: getIPSComplianceSummary(fund)
        }))
        .sort((a, b) => b.planSyncScore.score - a.planSyncScore.score);
};

// Range Input Component
const RangeInputFilter = ({ label, range, onChange, min, max, step, formatter, parser, suffix = '' }) => {
  return (
    <>
      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
        {label}
      </label>
      <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
        <InputNumber
          min={min}
          max={max}
          step={step}
          value={range[0]}
          onChange={(value) => {
            if (value !== null && value <= range[1]) {
              onChange([value, range[1]]);
            }
          }}
          formatter={formatter}
          parser={parser}
          style={{ width: '50%' }}
          placeholder="Min"
        />
        <InputNumber
          min={min}
          max={max}
          step={step}
          value={range[1]}
          onChange={(value) => {
            if (value !== null && value >= range[0]) {
              onChange([range[0], value]);
            }
          }}
          formatter={formatter}
          parser={parser}
          style={{ width: '50%' }}
          placeholder="Max"
        />
      </Space.Compact>
      <Slider
        range
        min={min}
        max={max}
        step={step}
        value={range}
        onChange={onChange}
        tooltip={{
          formatter: formatter || ((value) => `${value}${suffix}`)
        }}
      />
    </>
  );
};

// --- MAIN COMPONENT ---
const FundResearch = () => {
  // --- STATE MANAGEMENT ---
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFund, setSelectedFund] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [funds] = useState(mockFunds);
  const [searchResults, setSearchResults] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'compact'
  const [sortBy, setSortBy] = useState('score'); // 'score', 'expenseRatio', 'aum', 'sharpeRatio', 'alpha', 'name', 'ipsCompliance'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [favorites, setFavorites] = useState(new Set()); // Set of fund tickers that are favorited
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    fundType: [],
    activePassive: [],
    peerGroup: [],
    expenseRatioRange: [0, 2],
    aumRange: [0, 20000],
    sharpeRatioRange: [0, 2],
    alphaRange: [-2, 5],
    sharePriceRange: [0, 200],
    morningstarRating: [],
    scoreRange: [],
    ipsCompliance: [],
    favorites: 'All',
    // New comprehensive filters
    rSquaredRange: [0, 100],
    downsideCaptureRange: [0, 150],
    turnoverRatioRange: [0, 300],
    trackingErrorRange: [0, 50],
    managerTenureRange: [0, 30],
    has12b1Fee: 'All', // 'All', 'Yes', 'No'
    hasSalesLoad: 'All', // 'All', 'Yes', 'No'
    morningstarParentPillar: [] // 1-5 ratings
  });

  // --- DERIVED STATE & MEMOS ---
  const topFunds = useMemo(() => getTopFunds(funds), [funds]);
  
  // Statistics
  const statistics = useMemo(() => {
    if (!topFunds.length) return null;
    
    const totalAUM = topFunds.reduce((sum, fund) => sum + fund.metrics.fund_aum_millions, 0);
    const avgScore = topFunds.reduce((sum, fund) => sum + fund.planSyncScore.score, 0) / topFunds.length;
    const avgExpenseRatio = topFunds.reduce((sum, fund) => sum + fund.metrics.expense_ratio, 0) / topFunds.length;
    const avgIPSCompliance = topFunds.reduce((sum, fund) => sum + fund.ipsComplianceSummary.compliance_rate, 0) / topFunds.length;
    
    return {
      totalFunds: topFunds.length,
      totalAUM,
      avgScore,
      avgExpenseRatio,
      avgIPSCompliance
    };
  }, [topFunds]);


  
  // Sorting function
  const sortFunds = useCallback((funds, sortBy, sortOrder) => {
    return [...funds].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'score':
          aValue = a.planSyncScore.score;
          bValue = b.planSyncScore.score;
          break;
        case 'expenseRatio':
          aValue = a.metrics.expense_ratio;
          bValue = b.metrics.expense_ratio;
          break;
        case 'aum':
          aValue = a.metrics.fund_aum_millions;
          bValue = b.metrics.fund_aum_millions;
          break;
        case 'sharpeRatio':
          aValue = a.metrics.sharpe_ratio_5yr;
          bValue = b.metrics.sharpe_ratio_5yr;
          break;
        case 'alpha':
          aValue = a.metrics.alpha_5yr;
          bValue = b.metrics.alpha_5yr;
          break;
        case 'name':
          aValue = a.fund_name.toLowerCase();
          bValue = b.fund_name.toLowerCase();
          break;
        case 'ipsCompliance':
          aValue = a.ipsComplianceSummary.compliance_rate;
          bValue = b.ipsComplianceSummary.compliance_rate;
          break;
        case 'morningstar':
          aValue = a.qualitative_flags.morningstar_parent_pillar;
          bValue = b.qualitative_flags.morningstar_parent_pillar;
          break;
        case 'favorites':
          aValue = favorites.has(a.ticker) ? 1 : 0;
          bValue = favorites.has(b.ticker) ? 1 : 0;
          break;
        case 'sharePrice':
          aValue = a.metrics.share_price;
          bValue = b.metrics.share_price;
          break;
        case 'rSquared':
          aValue = a.metrics.r_squared_5yr;
          bValue = b.metrics.r_squared_5yr;
          break;
        case 'downsideCapture':
          aValue = a.metrics.downside_capture_5yr;
          bValue = b.metrics.downside_capture_5yr;
          break;
        case 'turnoverRatio':
          aValue = a.metrics.turnover_ratio;
          bValue = b.metrics.turnover_ratio;
          break;
        case 'trackingError':
          aValue = a.metrics.tracking_error_5yr;
          bValue = b.metrics.tracking_error_5yr;
          break;
        case 'managerTenure':
          aValue = a.metrics.manager_tenure_avg_yrs;
          bValue = b.metrics.manager_tenure_avg_yrs;
          break;
        default:
          aValue = a.planSyncScore.score;
          bValue = b.planSyncScore.score;
      }
      
      if (typeof aValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [favorites]);
  
  // Filtered and sorted funds based on current filters and sort settings
  const filteredFunds = useMemo(() => {
    let filtered = topFunds.filter(fund => {
      // Fund Type filter
      if (filters.fundType.length > 0 && !filters.fundType.includes(fund.fund_type)) return false;
      
      // Active/Passive filter
      if (filters.activePassive.length > 0 && !filters.activePassive.includes(fund.active_or_passive)) return false;
      
      // Peer Group filter
      if (filters.peerGroup.length > 0 && !filters.peerGroup.includes(fund.peer_group_details.name)) return false;
      
      // Expense Ratio filter (range)
      if (fund.metrics.expense_ratio < filters.expenseRatioRange[0] / 100 || fund.metrics.expense_ratio > filters.expenseRatioRange[1] / 100) return false;
      
      // AUM filter (range)
      if (fund.metrics.fund_aum_millions < filters.aumRange[0] || fund.metrics.fund_aum_millions > filters.aumRange[1]) return false;
      
      // Sharpe Ratio filter (range)
      if (fund.metrics.sharpe_ratio_5yr < filters.sharpeRatioRange[0] || fund.metrics.sharpe_ratio_5yr > filters.sharpeRatioRange[1]) return false;
      
      // Alpha filter (range)
      if (fund.metrics.alpha_5yr < filters.alphaRange[0] / 100 || fund.metrics.alpha_5yr > filters.alphaRange[1] / 100) return false;
      
      // Share Price filter (range)
      if (fund.metrics.share_price < filters.sharePriceRange[0] || fund.metrics.share_price > filters.sharePriceRange[1]) return false;
      
      // R-Squared filter (range) - convert to percentage
      if (fund.metrics.r_squared_5yr * 100 < filters.rSquaredRange[0] || fund.metrics.r_squared_5yr * 100 > filters.rSquaredRange[1]) return false;
      
      // Downside Capture filter (range) - convert to percentage
      if (fund.metrics.downside_capture_5yr * 100 < filters.downsideCaptureRange[0] || fund.metrics.downside_capture_5yr * 100 > filters.downsideCaptureRange[1]) return false;
      
      // Turnover Ratio filter (range) - convert to percentage
      if (fund.metrics.turnover_ratio * 100 < filters.turnoverRatioRange[0] || fund.metrics.turnover_ratio * 100 > filters.turnoverRatioRange[1]) return false;
      
      // Tracking Error filter (range) - convert to percentage
      if (fund.metrics.tracking_error_5yr * 100 < filters.trackingErrorRange[0] || fund.metrics.tracking_error_5yr * 100 > filters.trackingErrorRange[1]) return false;
      
      // Manager Tenure filter (range)
      if (fund.metrics.manager_tenure_avg_yrs < filters.managerTenureRange[0] || fund.metrics.manager_tenure_avg_yrs > filters.managerTenureRange[1]) return false;
      
      // 12b-1 Fee filter
      if (filters.has12b1Fee === 'Yes' && !fund.metrics.has_12b1_fee) return false;
      if (filters.has12b1Fee === 'No' && fund.metrics.has_12b1_fee) return false;
      
      // Sales Load filter
      if (filters.hasSalesLoad === 'Yes' && !fund.metrics.has_sales_load) return false;
      if (filters.hasSalesLoad === 'No' && fund.metrics.has_sales_load) return false;
      
      // Morningstar Parent Pillar filter
      if (filters.morningstarParentPillar.length > 0 && !filters.morningstarParentPillar.includes(fund.qualitative_flags.morningstar_parent_pillar)) return false;
      
      // Morningstar Rating filter (legacy - keeping for backward compatibility)
      if (filters.morningstarRating.length > 0 && !filters.morningstarRating.includes(fund.qualitative_flags.morningstar_parent_pillar)) return false;
      
      // Score Range filter
      if (filters.scoreRange.length > 0) {
        const score = fund.planSyncScore.score;
        const matchesRange = filters.scoreRange.some(range => {
          switch (range) {
            case 'excellent': return score >= 80;
            case 'good': return score >= 60 && score < 80;
            case 'fair': return score >= 40 && score < 60;
            case 'poor': return score < 40;
            default: return false;
          }
        });
        if (!matchesRange) return false;
      }
      
      // IPS Compliance filter
      if (filters.ipsCompliance.length > 0) {
        const complianceRate = fund.ipsComplianceSummary.compliance_rate;
        const matchesCompliance = filters.ipsCompliance.some(level => {
          switch (level) {
            case 'high': return complianceRate >= 75;
            case 'medium': return complianceRate >= 50 && complianceRate < 75;
            case 'low': return complianceRate < 50;
            default: return false;
          }
        });
        if (!matchesCompliance) return false;
      }
      
      // Favorites filter
      if (filters.favorites === 'favorites' && !favorites.has(fund.ticker)) return false;
      
      return true;
    });
    
    // Apply sorting
    return sortFunds(filtered, sortBy, sortOrder);
  }, [topFunds, filters, sortBy, sortOrder, sortFunds, favorites]);

  // Filtered Statistics - updates based on current filters
  const filteredStatistics = useMemo(() => {
    if (!filteredFunds.length) return null;
    
    const avgScore = filteredFunds.reduce((sum, fund) => sum + fund.planSyncScore.score, 0) / filteredFunds.length;
    const avgExpenseRatio = filteredFunds.reduce((sum, fund) => sum + fund.metrics.expense_ratio, 0) / filteredFunds.length;
    const avgIPSCompliance = filteredFunds.reduce((sum, fund) => sum + fund.ipsComplianceSummary.compliance_rate, 0) / filteredFunds.length;
    
    // Calculate peer group distribution
    const peerGroupCounts = {};
    filteredFunds.forEach(fund => {
      const peerGroup = fund.peer_group_details.name;
      peerGroupCounts[peerGroup] = (peerGroupCounts[peerGroup] || 0) + 1;
    });
    
    // Get most common peer group
    const mostCommonPeerGroup = Object.keys(peerGroupCounts).reduce((a, b) => 
      peerGroupCounts[a] > peerGroupCounts[b] ? a : b
    );
    
    return {
      totalFunds: filteredFunds.length,
      avgScore,
      avgExpenseRatio,
      avgIPSCompliance,
      peerGroupCounts,
      mostCommonPeerGroup,
      uniquePeerGroups: Object.keys(peerGroupCounts).length
    };
  }, [filteredFunds]);

  // Calculate filter option counts
  const filterCounts = useMemo(() => {
    const counts = {
      fundType: {},
      activePassive: {},
      peerGroup: {},
      morningstarRating: {},
      morningstarParentPillar: {},
      scoreRange: {},
      ipsCompliance: {},
      has12b1Fee: { Yes: 0, No: 0 },
      hasSalesLoad: { Yes: 0, No: 0 }
    };

    topFunds.forEach(fund => {
      // Fund Type counts
      counts.fundType[fund.fund_type] = (counts.fundType[fund.fund_type] || 0) + 1;
      
      // Active/Passive counts
      counts.activePassive[fund.active_or_passive] = (counts.activePassive[fund.active_or_passive] || 0) + 1;
      
      // Peer Group counts
      counts.peerGroup[fund.peer_group_details.name] = (counts.peerGroup[fund.peer_group_details.name] || 0) + 1;
      
      // Morningstar Rating counts (legacy)
      const rating = fund.qualitative_flags.morningstar_parent_pillar;
      counts.morningstarRating[rating] = (counts.morningstarRating[rating] || 0) + 1;
      
      // Morningstar Parent Pillar counts
      counts.morningstarParentPillar[rating] = (counts.morningstarParentPillar[rating] || 0) + 1;
      
      // Score Range counts
      const score = fund.planSyncScore.score;
      if (score >= 80) counts.scoreRange.excellent = (counts.scoreRange.excellent || 0) + 1;
      if (score >= 60 && score < 80) counts.scoreRange.good = (counts.scoreRange.good || 0) + 1;
      if (score >= 40 && score < 60) counts.scoreRange.fair = (counts.scoreRange.fair || 0) + 1;
      if (score < 40) counts.scoreRange.poor = (counts.scoreRange.poor || 0) + 1;
      
      // IPS Compliance counts
      const complianceRate = fund.ipsComplianceSummary.compliance_rate;
      if (complianceRate >= 75) counts.ipsCompliance.high = (counts.ipsCompliance.high || 0) + 1;
      if (complianceRate >= 50 && complianceRate < 75) counts.ipsCompliance.medium = (counts.ipsCompliance.medium || 0) + 1;
      if (complianceRate < 50) counts.ipsCompliance.low = (counts.ipsCompliance.low || 0) + 1;
      
      // 12b-1 Fee counts
      if (fund.metrics.has_12b1_fee) {
        counts.has12b1Fee.Yes += 1;
      } else {
        counts.has12b1Fee.No += 1;
      }
      
      // Sales Load counts
      if (fund.metrics.has_sales_load) {
        counts.hasSalesLoad.Yes += 1;
      } else {
        counts.hasSalesLoad.No += 1;
      }
    });

    return counts;
  }, [topFunds]);
  
  // --- EFFECTS ---
  useEffect(() => {
    if (searchTerm) {
        const results = funds.filter(fund => 
            fund.fund_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            fund.ticker.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setSearchResults(results);
        setSelectedSearchIndex(-1);
    } else {
        setSearchResults([]);
        setSelectedSearchIndex(-1);
    }
  }, [searchTerm, funds]);

  // Handle keyboard navigation for search results
  const handleSearchKeyDown = (e) => {
    if (searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSearchIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSearchIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSearchIndex >= 0 && selectedSearchIndex < searchResults.length) {
          handleSelectFund(searchResults[selectedSearchIndex]);
        }
        break;
      case 'Escape':
        setSearchResults([]);
        setSelectedSearchIndex(-1);
        break;
      default:
        break;
    }
  };

  // --- HANDLERS ---
  const handleSelectFund = useCallback((fund) => {
    const enhancedFund = {
      ...fund,
      planSyncScore: calculateFundScore(fund),
      ipsComplianceSummary: getIPSComplianceSummary(fund)
    };
    
    setSelectedFund(enhancedFund);
    setModalVisible(true);
    setSearchTerm('');
    setSearchResults([]);
    setSelectedSearchIndex(-1);
  }, []);

  const handleFilterChange = useCallback((filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      fundType: [],
      activePassive: [],
      peerGroup: [],
      expenseRatioRange: [0, 2],
      aumRange: [0, 20000],
      sharpeRatioRange: [0, 2],
      alphaRange: [-2, 5],
      sharePriceRange: [0, 200],
      morningstarRating: [],
      scoreRange: [],
      ipsCompliance: [],
      favorites: 'All',
      // New comprehensive filters
      rSquaredRange: [0, 100],
      downsideCaptureRange: [0, 150],
      turnoverRatioRange: [0, 300],
      trackingErrorRange: [0, 50],
      managerTenureRange: [0, 30],
      has12b1Fee: 'All', // 'All', 'Yes', 'No'
      hasSalesLoad: 'All', // 'All', 'Yes', 'No'
      morningstarParentPillar: [] // 1-5 ratings
    });
  }, []);

  const handleToggleFavorite = useCallback((fundTicker) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(fundTicker)) {
        newFavorites.delete(fundTicker);
      } else {
        newFavorites.add(fundTicker);
      }
      return newFavorites;
    });
  }, []);

  const handleSortChange = useCallback((newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);

  const activeFilterCount = Object.entries(filters).reduce((count, [key, filter]) => {
    if (Array.isArray(filter)) {
      return count + (filter.length > 0 ? 1 : 0);
    } else if (key === 'favorites' && filter === 'favorites') {
      return count + 1;
    } else if (key === 'has12b1Fee' && filter !== 'All') {
      return count + 1;
    } else if (key === 'hasSalesLoad' && filter !== 'All') {
      return count + 1;
    } else if (key.includes('Range') && Array.isArray(filter)) {
      // Check if range filter is different from default
      const defaultRanges = {
        expenseRatioRange: [0, 2],
        aumRange: [0, 20000],
        sharpeRatioRange: [0, 2],
        alphaRange: [-2, 5],
        sharePriceRange: [0, 200],
        rSquaredRange: [0, 100],
        downsideCaptureRange: [0, 150],
        turnoverRatioRange: [0, 300],
        trackingErrorRange: [0, 50],
        managerTenureRange: [0, 30]
      };
      const defaultRange = defaultRanges[key];
      if (defaultRange && (filter[0] !== defaultRange[0] || filter[1] !== defaultRange[1])) {
        return count + 1;
      }
    }
    return count;
  }, 0);

  // Table columns for list view
  const columns = [
    {
      title: 'Fund Name',
      dataIndex: 'fund_name',
      key: 'fund_name',
      width: 300,
      render: (text, record) => (
        <div>
          <Space>
            <Button
              type="text"
              icon={<StarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleFavorite(record.ticker);
              }}
              style={{ 
                color: favorites.has(record.ticker) ? '#faad14' : '#d9d9d9',
                padding: 0
              }}
            />
            <div>
              <Text strong style={{ fontSize: '14px', color: '#1a202c' }}>{text}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>{record.ticker}</Text>
            </div>
          </Space>
        </div>
      ),
      sorter: (a, b) => a.fund_name.localeCompare(b.fund_name),
    },
    {
      title: 'Type',
      key: 'type',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.fund_type === 'MF' ? 'blue' : 'purple'}>{record.fund_type}</Tag>
          <Tag color={record.active_or_passive === 'Active' ? 'green' : 'orange'}>
            {record.active_or_passive}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Peer Group',
      key: 'peer_group',
      width: 180,
      render: (_, record) => (
        <div>
          <Text strong style={{ fontSize: '12px' }}>
            {record.peer_group_details.name.replace('Morningstar ', '')}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.peer_group_details.fund_count} funds
          </Text>
        </div>
      ),
      sorter: (a, b) => a.peer_group_details.name.localeCompare(b.peer_group_details.name),
    },
    {
      title: 'PlanSync Score',
      key: 'score',
      width: 150,
      render: (_, record) => {
        const score = record.planSyncScore.score;
        const color = score >= 70 ? '#52c41a' : score >= 50 ? '#faad14' : '#f5222d';
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color }}>{score}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.planSyncScore.label}</div>
          </div>
        );
      },
      sorter: (a, b) => a.planSyncScore.score - b.planSyncScore.score,
    },
    {
      title: 'Expense Ratio',
      key: 'expense_ratio',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{formatPercentage(record.metrics.expense_ratio)}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.peer_percentile.expense_ratio}th percentile
          </Text>
        </Space>
      ),
      sorter: (a, b) => a.metrics.expense_ratio - b.metrics.expense_ratio,
    },
    {
      title: 'AUM',
      key: 'aum',
      width: 120,
      render: (_, record) => (
        <Text strong>{formatCurrency(record.metrics.fund_aum_millions)}</Text>
      ),
      sorter: (a, b) => a.metrics.fund_aum_millions - b.metrics.fund_aum_millions,
    },
    {
      title: 'Sharpe Ratio',
      key: 'sharpe',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.metrics.sharpe_ratio_5yr.toFixed(2)}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.peer_percentile.sharpe_ratio_5yr}th percentile
          </Text>
        </Space>
      ),
      sorter: (a, b) => a.metrics.sharpe_ratio_5yr - b.metrics.sharpe_ratio_5yr,
    },
    {
      title: 'IPS Compliance',
      key: 'ips_compliance',
      width: 150,
      render: (_, record) => {
        const rate = record.ipsComplianceSummary.compliance_rate;
        const color = rate >= 75 ? '#52c41a' : rate >= 50 ? '#faad14' : '#f5222d';
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color }}>{rate.toFixed(0)}%</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.ipsComplianceSummary.compliant_count}/{record.ipsComplianceSummary.total_companies}
            </div>
          </div>
        );
      },
      sorter: (a, b) => a.ipsComplianceSummary.compliance_rate - b.ipsComplianceSummary.compliance_rate,
    },
    {
      title: 'Morningstar',
      key: 'morningstar',
      width: 120,
      render: (_, record) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#faad14', fontSize: '16px' }}>
            {formatStars(record.qualitative_flags.morningstar_parent_pillar)}
          </div>
        </div>
      ),
      sorter: (a, b) => a.qualitative_flags.morningstar_parent_pillar - b.qualitative_flags.morningstar_parent_pillar,
    },
  ];

  // Render fund details modal
  const renderFundDetails = () => {
    if (!selectedFund) return null;

    const metricsGrid = [
      { label: "Expense Ratio", value: formatPercentage(selectedFund.metrics.expense_ratio), percentile: selectedFund.peer_percentile.expense_ratio, lowerIsBetter: true },
      { label: "Share Price", value: formatSharePrice(selectedFund.metrics.share_price), percentile: null },
      { label: "5-Yr Sharpe Ratio", value: selectedFund.metrics.sharpe_ratio_5yr.toFixed(2), percentile: selectedFund.peer_percentile.sharpe_ratio_5yr },
      { label: "5-Yr Alpha", value: formatPercentage(selectedFund.metrics.alpha_5yr), percentile: selectedFund.peer_percentile.alpha_5yr },
      { label: "AUM", value: formatCurrency(selectedFund.metrics.fund_aum_millions), percentile: null },
    ];

  return (
      <div style={{ padding: 0, background: 'transparent', minHeight: '95vh' }}>
        {/* Premium Header Section */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          padding: '40px',
          borderRadius: '10px 10px 0 0',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Row align="middle" justify="space-between">
            <Col span={16}>
              <Title level={2} style={{ color: 'white', marginBottom: '8px', fontSize: '32px' }}>
                {selectedFund.fund_name}
              </Title>
              <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: '18px', marginBottom: '16px' }}>
                {selectedFund.ticker} • {selectedFund.fund_type} • {selectedFund.active_or_passive}
              </Paragraph>
              <Space size="large" wrap>
                <Tag icon={<HomeOutlined />} color="white" style={{ padding: '6px 16px', fontSize: '14px', background: 'rgba(255,255,255,0.2)', border: 'none' }}>
                  {selectedFund.peer_group_details.name}
                </Tag>
                <Tag icon={<TeamOutlined />} style={{ padding: '6px 16px', fontSize: '14px', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white' }}>
                  {selectedFund.peer_group_details.fund_count} funds in peer group
                </Tag>
              </Space>
            </Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '12px', padding: '20px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>PLANSYNC SCORE</Text>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'white' }}>{selectedFund.planSyncScore.score}</div>
                </div>
                <div>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>RATING</Text>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>{selectedFund.planSyncScore.label}</div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Key Metrics Cards */}
        <div style={{ padding: '0 40px', marginTop: '-30px', marginBottom: '20px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center', height: '100%' }}>
                <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <Avatar style={{ backgroundColor: '#667eea', marginBottom: '12px' }} size={48} icon={<DollarOutlined />} />
                    <Statistic
                      title="Share Price"
                      value={selectedFund.metrics.share_price}
                      formatter={(value) => formatSharePrice(value)}
                      valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ height: '6px', backgroundColor: '#f0f0f0', borderRadius: '3px', marginBottom: '8px' }}>
                      <div style={{ height: '100%', backgroundColor: '#667eea', borderRadius: '3px', width: '0%' }}></div>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Current market price
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center', height: '100%' }}>
                <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <Avatar style={{ backgroundColor: '#764ba2', marginBottom: '12px' }} size={48} icon={<PercentageOutlined />} />
                    <Statistic
                      title="Expense Ratio"
                      value={selectedFund.metrics.expense_ratio * 100}
                      suffix="%"
                      precision={2}
                      valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Progress 
                      percent={100 - selectedFund.peer_percentile.expense_ratio} 
                      strokeColor="#764ba2" 
                      showInfo={false}
                      style={{ marginBottom: '8px' }}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {selectedFund.peer_percentile.expense_ratio}th percentile
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center', height: '100%' }}>
                <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <Avatar style={{ backgroundColor: '#52c41a', marginBottom: '12px' }} size={48} icon={<TrophyOutlined />} />
                    <Statistic
                      title="Sharpe Ratio (5yr)"
                      value={selectedFund.metrics.sharpe_ratio_5yr}
                      precision={2}
                      valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}
                    />
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Progress 
                      percent={selectedFund.peer_percentile.sharpe_ratio_5yr} 
                      strokeColor="#52c41a" 
                      showInfo={false}
                      style={{ marginBottom: '8px' }}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {selectedFund.peer_percentile.sharpe_ratio_5yr}th percentile
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center', height: '100%' }}>
                <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <Avatar style={{ backgroundColor: '#1890ff', marginBottom: '12px' }} size={48} icon={<SafetyCertificateOutlined />} />
                    <Statistic
                      title="IPS Compliance"
                      value={selectedFund.ipsComplianceSummary.compliance_rate}
                      suffix="%"
                      precision={0}
                      valueStyle={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: selectedFund.ipsComplianceSummary.compliance_rate >= 75 ? '#52c41a' : selectedFund.ipsComplianceSummary.compliance_rate >= 50 ? '#faad14' : '#f5222d'
                      }}
                    />
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Progress 
                      percent={selectedFund.ipsComplianceSummary.compliance_rate} 
                      strokeColor={selectedFund.ipsComplianceSummary.compliance_rate >= 75 ? '#52c41a' : selectedFund.ipsComplianceSummary.compliance_rate >= 50 ? '#faad14' : '#f5222d'}
                      showInfo={false}
                      style={{ marginBottom: '8px' }}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {selectedFund.ipsComplianceSummary.compliant_count}/{selectedFund.ipsComplianceSummary.total_companies} companies
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
            
        {/* Tabbed Content */}
        <div style={{ padding: '0 40px 40px' }}>
          <Tabs 
            defaultActiveKey="overview" 
            size="large"
            style={{ 
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}
            items={[
              {
                key: 'overview',
                label: (
                  <span>
                    <BarChartOutlined /> Overview
                  </span>
                ),
                children: (
                  <div>
                    <Row gutter={[24, 24]}>
                      <Col span={12}>
                        <Card title={<span><InfoCircleOutlined /> Fund Details</span>} style={{ borderRadius: '8px' }}>
                          <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text type="secondary">Fund Type</Text>
                              <Text strong>{selectedFund.fund_type}</Text>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text type="secondary">Management Style</Text>
                              <Text strong>{selectedFund.active_or_passive}</Text>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text type="secondary">Assets Under Management</Text>
                              <Text strong>{formatCurrency(selectedFund.metrics.fund_aum_millions)}</Text>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text type="secondary">Manager Tenure</Text>
                              <Text strong>{formatYears(selectedFund.metrics.manager_tenure_avg_yrs)}</Text>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text type="secondary">Morningstar Rating</Text>
                              <Text strong style={{ color: '#faad14' }}>
                                {formatStars(selectedFund.qualitative_flags.morningstar_parent_pillar)}
                              </Text>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                      
                      <Col span={12}>
                        <Card title={<span><FundOutlined /> Performance Metrics</span>} style={{ height: '100%', borderRadius: '8px' }}>
                          <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            {metricsGrid.map(metric => (
                              <div key={metric.label}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                  <Text type="secondary">{metric.label}</Text>
                                  <Text strong>{metric.value}</Text>
                                </div>
                                {metric.percentile && (
                                  <div>
                                    <Progress 
                                      percent={metric.lowerIsBetter ? (100 - metric.percentile) : metric.percentile} 
                                      strokeColor={metric.lowerIsBetter ? 
                                        (metric.percentile <= 25 ? '#52c41a' : metric.percentile <= 50 ? '#faad14' : '#f5222d') :
                                        (metric.percentile >= 75 ? '#52c41a' : metric.percentile >= 50 ? '#faad14' : '#f5222d')
                                      }
                                      showInfo={false}
                                      size="small"
                                    />
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      {metric.percentile}th percentile
                                    </Text>
                                  </div>
            )}
          </div>
                            ))}
                          </Space>
                        </Card>
                      </Col>
                    </Row>
        </div>
                )
              },
              {
                key: 'compliance',
                label: (
                  <span>
                    <SafetyCertificateOutlined /> IPS Compliance
                  </span>
                ),
                children: (
                  <div>
                    <Alert
                      message="IPS Compliance Analysis"
                      description={`This fund has been evaluated against ${selectedFund.ipsComplianceSummary.total_companies} company IPS criteria. It meets requirements for ${selectedFund.ipsComplianceSummary.compliant_count} companies and fails ${selectedFund.ipsComplianceSummary.non_compliant_count} companies.`}
                      type="info"
                      showIcon
                      style={{ marginBottom: '24px' }}
                    />
                    
                    <Row gutter={[24, 24]}>
                      <Col span={12}>
                        {selectedFund.ipsComplianceSummary.compliant_companies.length > 0 && (
                          <Card title={<span style={{ color: '#52c41a' }}><CheckCircleOutlined /> Compliant Companies</span>} style={{ borderRadius: '8px' }}>
                            <List
                              dataSource={selectedFund.ipsComplianceSummary.compliant_companies}
                              renderItem={item => (
                                <List.Item>
                                  <List.Item.Meta
                                    avatar={<Avatar style={{ backgroundColor: '#52c41a' }} icon={<CheckCircleOutlined />} />}
                                    title={item.company.name}
                                    description="Meets all IPS criteria"
                                  />
                                  <Tag color="success">✓ Compliant</Tag>
                                </List.Item>
                              )}
                            />
                          </Card>
                        )}
                      </Col>
                      
                      <Col span={12}>
                        {selectedFund.ipsComplianceSummary.non_compliant_companies.length > 0 && (
                          <Card title={<span style={{ color: '#f5222d' }}><ExclamationCircleOutlined /> Non-Compliant Companies</span>} style={{ borderRadius: '8px' }}>
                            <List
                              dataSource={selectedFund.ipsComplianceSummary.non_compliant_companies}
                              renderItem={item => (
                                <List.Item>
                                  <List.Item.Meta
                                    avatar={<Avatar style={{ backgroundColor: '#f5222d' }} icon={<ExclamationCircleOutlined />} />}
                                    title={item.company.name}
                                    description={
                                      <div>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                          {item.compliance.issues.length} issue(s) found
                                        </Text>
                                        <ul style={{ marginTop: '4px', fontSize: '12px' }}>
                                          {item.compliance.issues.slice(0, 2).map((issue, index) => (
                                            <li key={index}>{issue}</li>
                                          ))}
                                          {item.compliance.issues.length > 2 && (
                                            <li>+ {item.compliance.issues.length - 2} more...</li>
                                          )}
                                        </ul>
          </div>
                                    }
                                  />
                                  <Tag color="error">✗ Non-Compliant</Tag>
                                </List.Item>
                              )}
                            />
                          </Card>
                        )}
                      </Col>
                    </Row>
              </div>
                )
              },
              {
                key: 'risk',
                label: (
                  <span>
                    <WarningOutlined /> Risk Analysis
                  </span>
                ),
                children: (
                  <div>
                    <Row gutter={[24, 24]}>
                      <Col span={8}>
                        <Card title="Risk Metrics" style={{ borderRadius: '8px' }}>
                          <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div>
                              <Text type="secondary">R-Squared (5yr)</Text>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>
                                {formatPercentage(selectedFund.metrics.r_squared_5yr, 0)}
            </div>
          </div>
                            <div>
                              <Text type="secondary">Downside Capture (5yr)</Text>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>
                                {selectedFund.metrics.downside_capture_5yr.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <Text type="secondary">Tracking Error (5yr)</Text>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>
                                {formatPercentage(selectedFund.metrics.tracking_error_5yr, 0)}
                              </div>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                      
                      <Col span={8}>
                        <Card title="Portfolio Details" style={{ borderRadius: '8px' }}>
                          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
                              <Text type="secondary">Turnover Ratio</Text>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>
                                {formatPercentage(selectedFund.metrics.turnover_ratio, 0)}
                      </div>
                  </div>
                            <div>
                              <Text type="secondary">12b-1 Fee</Text>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>
                                {selectedFund.metrics.has_12b1_fee ? (
                                  <Tag color="error">Present</Tag>
                                ) : (
                                  <Tag color="success">None</Tag>
                                )}
                              </div>
                            </div>
                            <div>
                              <Text type="secondary">Sales Load</Text>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>
                                {selectedFund.metrics.has_sales_load ? (
                                  <Tag color="error">Present</Tag>
                                ) : (
                                  <Tag color="success">None</Tag>
                                )}
                              </div>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                      
                      <Col span={8}>
                        <Card title="Performance Summary" style={{ borderRadius: '8px' }}>
                          <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div>
                              <Text type="secondary">Alpha (5yr)</Text>
                              <div style={{ 
                                fontSize: '20px', 
                                fontWeight: 'bold', 
                                marginTop: '4px',
                                color: selectedFund.metrics.alpha_5yr >= 0 ? '#52c41a' : '#f5222d'
                              }}>
                                {formatPercentage(selectedFund.metrics.alpha_5yr)}
                              </div>
                            </div>
                            <div>
                              <Text type="secondary">Peer Rank (Alpha)</Text>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>
                                {selectedFund.peer_percentile.alpha_5yr}th percentile
                              </div>
                            </div>
                            <div>
                              <Text type="secondary">Peer Rank (Sharpe)</Text>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '4px' }}>
                                {selectedFund.peer_percentile.sharpe_ratio_5yr}th percentile
                              </div>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
    );
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#6366f1',
          borderRadius: 8,
        },
      }}
    >
      <PageContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Search Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <SearchSection>
            <Space direction="vertical" size={16} style={{ width: '100%', padding: '24px' }}>
              <div style={{ position: 'relative' }}>
                <Input
                  size="large"
                  placeholder="Search by fund name or ticker..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  style={{ borderRadius: 8 }}
                />
                {searchResults.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #d9d9d9',
                    borderRadius: '8px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    {searchResults.map((fund, index) => (
                      <div 
                        key={fund.ticker}
                        onClick={() => handleSelectFund(fund)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: index < searchResults.length - 1 ? '1px solid #f0f0f0' : 'none',
                          backgroundColor: selectedSearchIndex === index ? '#f5f5f5' : 'white'
                        }}
                      >
                        <div style={{ fontWeight: 'bold' }}>{fund.fund_name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {fund.ticker} • {fund.fund_type} • {fund.active_or_passive}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Space>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setShowFilters(!showFilters)}
                  type={showFilters ? 'primary' : 'default'}
                >
                  Advanced Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                </Button>
                
                <Space>
                  <Text type="secondary">View:</Text>
                  <ViewModeButton
                    active={viewMode === 'grid'}
                    onClick={() => setViewMode('grid')}
                    icon={<AppstoreOutlined />}
                  />
                  <ViewModeButton
                    active={viewMode === 'list'}
                    onClick={() => setViewMode('list')}
                    icon={<TableOutlined />}
                  />
                </Space>
                
                <Space>
                  <Text type="secondary">Sort:</Text>
                  <Select
                    value={sortBy}
                    onChange={(value) => handleSortChange(value, sortOrder)}
                    style={{ width: 180 }}
                  >
                    <Option value="score">PlanSync Score</Option>
                    <Option value="expenseRatio">Expense Ratio</Option>
                    <Option value="aum">AUM</Option>
                    <Option value="sharpeRatio">Sharpe Ratio</Option>
                    <Option value="alpha">Alpha</Option>
                    <Option value="ipsCompliance">IPS Compliance</Option>
                    <Option value="name">Fund Name</Option>
                    <Option value="sharePrice">Share Price</Option>
                    <Option value="rSquared">R-Squared</Option>
                    <Option value="downsideCapture">Downside Capture</Option>
                    <Option value="turnoverRatio">Turnover Ratio</Option>
                    <Option value="trackingError">Tracking Error</Option>
                    <Option value="managerTenure">Manager Tenure</Option>
                    <Option value="morningstar">Morningstar Rating</Option>
                  </Select>
                  <Button
                    icon={<SortAscendingOutlined />}
                    onClick={() => handleSortChange(sortBy, sortOrder === 'asc' ? 'desc' : 'asc')}
                  >
                    {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  </Button>
                </Space>
                
                {activeFilterCount > 0 && (
                  <Button type="link" onClick={clearAllFilters}>
                    Clear all filters
                  </Button>
                )}
              </Space>
            </Space>
          </SearchSection>
        </motion.div>

        {/* Filters Section */}
        <AnimatePresence>
          {showFilters && (
            <FiltersSection
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Fund Type</label>
                  <Select
                    mode="multiple"
                    placeholder="Select fund types"
                    style={{ width: '100%' }}
                    value={filters.fundType}
                    onChange={(value) => handleFilterChange('fundType', value)}
                  >
                    <Option value="MF">Mutual Fund ({filterCounts.fundType.MF || 0})</Option>
                    <Option value="CIT">Collective Investment Trust ({filterCounts.fundType.CIT || 0})</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Management Style</label>
                  <Select
                    mode="multiple"
                    placeholder="Select management styles"
                    style={{ width: '100%' }}
                    value={filters.activePassive}
                    onChange={(value) => handleFilterChange('activePassive', value)}
                  >
                    <Option value="Active">Active ({filterCounts.activePassive.Active || 0})</Option>
                    <Option value="Passive">Passive ({filterCounts.activePassive.Passive || 0})</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Peer Group</label>
                  <Select
                    mode="multiple"
                    placeholder="Select peer groups"
                    style={{ width: '100%' }}
                    value={filters.peerGroup}
                    onChange={(value) => handleFilterChange('peerGroup', value)}
                  >
                    <Option value="Morningstar Large Growth">Large Growth ({filterCounts.peerGroup["Morningstar Large Growth"] || 0})</Option>
                    <Option value="Morningstar Large Value">Large Value ({filterCounts.peerGroup["Morningstar Large Value"] || 0})</Option>
                    <Option value="Morningstar Large Blend">Large Blend ({filterCounts.peerGroup["Morningstar Large Blend"] || 0})</Option>
                    <Option value="Morningstar Mid-Cap Growth">Mid-Cap Growth ({filterCounts.peerGroup["Morningstar Mid-Cap Growth"] || 0})</Option>
                    <Option value="Morningstar Mid-Cap Value">Mid-Cap Value ({filterCounts.peerGroup["Morningstar Mid-Cap Value"] || 0})</Option>
                    <Option value="Morningstar Mid-Cap Blend">Mid-Cap Blend ({filterCounts.peerGroup["Morningstar Mid-Cap Blend"] || 0})</Option>
                    <Option value="Morningstar Small Growth">Small Growth ({filterCounts.peerGroup["Morningstar Small Growth"] || 0})</Option>
                    <Option value="Morningstar Small Value">Small Value ({filterCounts.peerGroup["Morningstar Small Value"] || 0})</Option>
                    <Option value="Morningstar Small Blend">Small Blend ({filterCounts.peerGroup["Morningstar Small Blend"] || 0})</Option>
                    <Option value="Morningstar Intermediate-Term Bond">Intermediate Bond ({filterCounts.peerGroup["Morningstar Intermediate-Term Bond"] || 0})</Option>
                    <Option value="Morningstar Short-Term Bond">Short-Term Bond ({filterCounts.peerGroup["Morningstar Short-Term Bond"] || 0})</Option>
                    <Option value="Morningstar International Large-Cap Blend">International Large-Cap ({filterCounts.peerGroup["Morningstar International Large-Cap Blend"] || 0})</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Morningstar Rating</label>
                  <Select
                    mode="multiple"
                    placeholder="Select ratings"
                    style={{ width: '100%' }}
                    value={filters.morningstarRating}
                    onChange={(value) => handleFilterChange('morningstarRating', value)}
                  >
                    <Option value={5}>5 Stars ({filterCounts.morningstarRating[5] || 0})</Option>
                    <Option value={4}>4 Stars ({filterCounts.morningstarRating[4] || 0})</Option>
                    <Option value={3}>3 Stars ({filterCounts.morningstarRating[3] || 0})</Option>
                    <Option value={2}>2 Stars ({filterCounts.morningstarRating[2] || 0})</Option>
                    <Option value={1}>1 Star ({filterCounts.morningstarRating[1] || 0})</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Score Range</label>
                  <Select
                    mode="multiple"
                    placeholder="Select score ranges"
                    style={{ width: '100%' }}
                    value={filters.scoreRange}
                    onChange={(value) => handleFilterChange('scoreRange', value)}
                  >
                    <Option value="excellent">Excellent (80+) ({filterCounts.scoreRange.excellent || 0})</Option>
                    <Option value="good">Good (60-79) ({filterCounts.scoreRange.good || 0})</Option>
                    <Option value="fair">Fair (40-59) ({filterCounts.scoreRange.fair || 0})</Option>
                    <Option value="poor">Poor (&lt;40) ({filterCounts.scoreRange.poor || 0})</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>IPS Compliance</label>
                  <Select
                    mode="multiple"
                    placeholder="Select compliance levels"
                    style={{ width: '100%' }}
                    value={filters.ipsCompliance}
                    onChange={(value) => handleFilterChange('ipsCompliance', value)}
                  >
                    <Option value="high">High (75%+) ({filterCounts.ipsCompliance.high || 0})</Option>
                    <Option value="medium">Medium (50-74%) ({filterCounts.ipsCompliance.medium || 0})</Option>
                    <Option value="low">Low (&lt;50%) ({filterCounts.ipsCompliance.low || 0})</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Expense Ratio Range"
                    range={filters.expenseRatioRange}
                    onChange={(value) => handleFilterChange('expenseRatioRange', value)}
                    min={0}
                    max={2}
                    step={0.1}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    suffix="%"
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="AUM Range (in Millions)"
                    range={filters.aumRange}
                    onChange={(value) => handleFilterChange('aumRange', value)}
                    min={0}
                    max={20000}
                    step={100}
                    formatter={(value) => `$${value}M`}
                    parser={(value) => value.replace(/\$|M/g, '')}
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Sharpe Ratio Range"
                    range={filters.sharpeRatioRange}
                    onChange={(value) => handleFilterChange('sharpeRatioRange', value)}
                    min={0}
                    max={2}
                    step={0.1}
                    formatter={(value) => value?.toFixed(1)}
                    parser={(value) => parseFloat(value)}
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Alpha Range"
                    range={filters.alphaRange}
                    onChange={(value) => handleFilterChange('alphaRange', value)}
                    min={-2}
                    max={5}
                    step={0.1}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    suffix="%"
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Share Price Range"
                    range={filters.sharePriceRange}
                    onChange={(value) => handleFilterChange('sharePriceRange', value)}
                    min={0}
                    max={200}
                    step={1}
                    formatter={(value) => `$${value}`}
                    parser={(value) => value.replace('$', '')}
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="R-Squared Range"
                    range={filters.rSquaredRange}
                    onChange={(value) => handleFilterChange('rSquaredRange', value)}
                    min={0}
                    max={100}
                    step={1}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    suffix="%"
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Downside Capture Range"
                    range={filters.downsideCaptureRange}
                    onChange={(value) => handleFilterChange('downsideCaptureRange', value)}
                    min={0}
                    max={150}
                    step={1}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    suffix="%"
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Turnover Ratio Range"
                    range={filters.turnoverRatioRange}
                    onChange={(value) => handleFilterChange('turnoverRatioRange', value)}
                    min={0}
                    max={300}
                    step={5}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    suffix="%"
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Tracking Error Range"
                    range={filters.trackingErrorRange}
                    onChange={(value) => handleFilterChange('trackingErrorRange', value)}
                    min={0}
                    max={50}
                    step={0.5}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    suffix="%"
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Manager Tenure Range"
                    range={filters.managerTenureRange}
                    onChange={(value) => handleFilterChange('managerTenureRange', value)}
                    min={0}
                    max={30}
                    step={0.5}
                    formatter={(value) => `${value} years`}
                    parser={(value) => value.replace(' years', '')}
                    suffix=" years"
                  />
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>12b-1 Fees</label>
                  <Select
                    placeholder="Select 12b-1 fee status"
                    style={{ width: '100%' }}
                    value={filters.has12b1Fee}
                    onChange={(value) => handleFilterChange('has12b1Fee', value)}
                  >
                    <Option value="All">All Funds</Option>
                    <Option value="Yes">Has 12b-1 Fees ({filterCounts.has12b1Fee.Yes || 0})</Option>
                    <Option value="No">No 12b-1 Fees ({filterCounts.has12b1Fee.No || 0})</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Sales Loads</label>
                  <Select
                    placeholder="Select sales load status"
                    style={{ width: '100%' }}
                    value={filters.hasSalesLoad}
                    onChange={(value) => handleFilterChange('hasSalesLoad', value)}
                  >
                    <Option value="All">All Funds</Option>
                    <Option value="Yes">Has Sales Loads ({filterCounts.hasSalesLoad.Yes || 0})</Option>
                    <Option value="No">No Sales Loads ({filterCounts.hasSalesLoad.No || 0})</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Morningstar Parent Pillar</label>
                  <Select
                    mode="multiple"
                    placeholder="Select ratings"
                    style={{ width: '100%' }}
                    value={filters.morningstarParentPillar}
                    onChange={(value) => handleFilterChange('morningstarParentPillar', value)}
                  >
                    <Option value={5}>5 Stars ({filterCounts.morningstarParentPillar[5] || 0})</Option>
                    <Option value={4}>4 Stars ({filterCounts.morningstarParentPillar[4] || 0})</Option>
                    <Option value={3}>3 Stars ({filterCounts.morningstarParentPillar[3] || 0})</Option>
                    <Option value={2}>2 Stars ({filterCounts.morningstarParentPillar[2] || 0})</Option>
                    <Option value={1}>1 Star ({filterCounts.morningstarParentPillar[1] || 0})</Option>
                  </Select>
                </Col>
              </Row>
            </FiltersSection>
          )}
        </AnimatePresence>

        {/* Statistics Cards */}
        {filteredStatistics && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
                        <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={8}>
                <StatsCard gradient="#1e3a8a 0%, #3b82f6 100%">
                  <div>
                    <Text style={{ color: 'white', opacity: 0.8 }}>Filtered Results</Text>
                    <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                      {filteredStatistics.totalFunds}
                    </Title>
                  </div>
                </StatsCard>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <StatsCard gradient="#06b6d4 0%, #0891b2 100%">
                  <div>
                    <Text style={{ color: 'white', opacity: 0.8 }}>Avg Score</Text>
                    <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                      {filteredStatistics.avgScore.toFixed(1)}
                    </Title>
                  </div>
                </StatsCard>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <StatsCard gradient="#10b981 0%, #059669 100%">
                  <div>
                    <Text style={{ color: 'white', opacity: 0.8 }}>Peer Groups</Text>
                    <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                      {filteredStatistics.uniquePeerGroups}
                    </Title>
                  </div>
                </StatsCard>
              </Col>
            </Row>
          </motion.div>
        )}

        {/* Results Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {viewMode === 'grid' ? (
            <Row gutter={[16, 16]} style={{ display: 'flex', flexWrap: 'wrap' }}>
              {filteredFunds.map((fund) => (
                <Col xs={24} sm={12} lg={8} xl={6} key={fund.ticker} style={{ display: 'flex', marginBottom: '16px' }}>
                  <FundCard
                    onClick={() => handleSelectFund(fund)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Header Section */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <Title level={5} style={{ 
                          margin: 0, 
                          fontSize: '16px', 
                          lineHeight: '1.3',
                          height: '44px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {fund.fund_name}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                          {fund.ticker} • {fund.fund_type} • {fund.active_or_passive}
                        </Text>
                        <Text type="secondary" style={{ 
                          fontSize: '11px', 
                          color: '#666', 
                          display: 'block', 
                          marginTop: '2px',
                          height: '16px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {fund.peer_group_details.name}
                        </Text>
                      </div>
                      <Button
                        type="text"
                        icon={<StarOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(fund.ticker);
                        }}
                        style={{ 
                          color: favorites.has(fund.ticker) ? '#faad14' : '#d9d9d9',
                          padding: 0,
                          flexShrink: 0
                        }}
                      />
                    </div>
                    
                    {/* Score Section */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary">PlanSync Score</Text>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ 
                            fontSize: '24px', 
                            fontWeight: 'bold',
                            color: fund.planSyncScore.score >= 70 ? '#52c41a' : fund.planSyncScore.score >= 50 ? '#faad14' : '#f5222d'
                          }}>
                            {fund.planSyncScore.score}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {fund.planSyncScore.label}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Metrics Section - Flexible content */}
                    <div style={{ flex: 1, marginBottom: '20px' }}>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Text type="secondary">Expense Ratio</Text>
                          <Text strong>{formatPercentage(fund.metrics.expense_ratio)}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Text type="secondary">AUM</Text>
                          <Text strong>{formatCurrency(fund.metrics.fund_aum_millions)}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Text type="secondary">Sharpe Ratio</Text>
                          <Text strong>{fund.metrics.sharpe_ratio_5yr.toFixed(2)}</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Text type="secondary">IPS Compliance</Text>
                          <Text strong style={{ 
                            color: fund.ipsComplianceSummary.compliance_rate >= 75 ? '#52c41a' : 
                                  fund.ipsComplianceSummary.compliance_rate >= 50 ? '#faad14' : '#f5222d'
                          }}>
                            {fund.ipsComplianceSummary.compliance_rate.toFixed(0)}%
                          </Text>
                        </div>
                      </Space>
                    </div>
                    
                    {/* Footer Section - Always at bottom */}
                    <div style={{ textAlign: 'center', marginTop: 'auto' }}>
                      <div style={{ color: '#faad14', fontSize: '16px' }}>
                        {formatStars(fund.qualitative_flags.morningstar_parent_pillar)}
                      </div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Morningstar Rating
                      </Text>
                    </div>
                  </FundCard>
                </Col>
              ))}
            </Row>
          ) : (
            <GlassCard>
              <Table
                columns={columns}
                dataSource={filteredFunds}
                rowKey="ticker"
                loading={loading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} funds`,
                }}
                onRow={(record) => ({
                  onClick: () => handleSelectFund(record),
                  style: { cursor: 'pointer' }
                })}
                scroll={{ x: 1200 }}
              />
            </GlassCard>
          )}
        </motion.div>

        {/* Fund Details Modal */}
        <Modal
          title={null}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width="95vw"
          style={{ top: 20 }}
          bodyStyle={{ padding: 0, overflowY: 'auto' }}
          styles={{
            content: { padding: 0 },
            body: { padding: 0 },
          }}
          closeIcon={<CloseOutlined style={{ fontSize: '20px' }} />}
        >
          {renderFundDetails()}
        </Modal>
      </PageContainer>
    </ConfigProvider>
  );
};

export default FundResearch;